import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { usePlan } from '../context/PlanContext';
import { openCommandGlobal } from '../context/CommandContext';

import {
  Buildings,
  CaretDown,
  CaretRight,
  ChartBar,
  Clipboard,
  FileText,
  FlowArrow,
  GearSix,
  GitBranch,
  House,
  Lightning,
  ListDashes,
  Microphone,
  Question,
  Robot,
  Scales,
  SignOut,
  Sparkle,
  Target,
  TrendUp,
  UsersThree,
  X
} from '@phosphor-icons/react';
const MobileNav = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const { logout } = useAuth();
  const { plan: tier } = usePlan();
  const navigate = useNavigate();

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    setIsOpen(false);
  };

  const navSections = [
    {
      title: 'Dashboard',
      icon: House,
      path: '/dashboard',
      single: true
    },
    {
      title: 'AI Tools',
      icon: Robot,
      items: [
        { name: 'AI Workforce', path: '/partner-pro/ai-workforce/agents', icon: Lightning },
        { name: 'My Agents', path: '/partner-pro/ai-workforce/agents', icon: Robot },
        { name: 'Templates', path: '/partner-pro/ai-workforce/templates', icon: Sparkle },
        { name: 'Voice Agents', path: '/settings/voice', icon: Microphone },
        { name: 'Agent Builder', path: '/unified-agent-builder', icon: GitBranch }
      ]
    },
    {
      title: 'Processes',
      icon: FlowArrow,
      items: [
        { name: 'Assessments', path: '/assessments/wizard', icon: Clipboard },
        { name: 'Processes', path: '/processes', icon: FlowArrow },
        { name: 'Opportunities', path: '/opportunities', icon: Target },
        { name: 'Org Chart', path: '/org-chart', icon: GitBranch }
      ]
    },
    {
      title: 'Organisation',
      icon: Buildings,
      items: [
        { name: 'Org Settings', path: '/settings/admin', icon: GearSix },
        ...(tier === 'partner-pro' || tier === 'partner_pro' ? [
          { name: 'Org Admin', path: '/settings/admin', icon: Buildings }
        ] : []),
        { name: 'Team Members', path: '/settings/team', icon: UsersThree }
      ]
    },
    {
      title: 'Analytics',
      icon: ChartBar,
      items: [
        { name: 'AI Readiness', path: '/analytics', icon: TrendUp },
        { name: 'Usage Analytics', path: '/analytics', icon: ChartBar },
        { name: 'Voice Analytics', path: '/analytics/voice', icon: Microphone },
        { name: 'Agent Analytics', path: '/partner-pro/ai-workforce/analytics', icon: Robot }
      ]
    },
    {
      title: 'Templates',
      icon: FileText,
      items: [
        { name: 'Process Templates', path: '/process-templates', icon: FileText },
        { name: 'Opportunity Templates', path: '#', icon: Target, disabled: true },
        { name: 'Agent Templates', path: '#', icon: Robot, disabled: true }
      ]
    },
    {
      title: 'More',
      icon: CaretDown,
      items: [
        { name: 'Account Settings', path: '/account', icon: GearSix },
        { name: 'Help & Support', path: '/contact', icon: Question },
        { name: 'Legal', path: '/terms', icon: Scales }
      ]
    }
  ];

  return (
    <>
      {/* Hamburger Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="lg:hidden p-2 text-white hover:text-[#14F195] transition-colors"
        aria-label="Open menu"
      >
        <ListDashes size={24} />
      </button>

      {/* Mobile Drawer */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 lg:hidden"
            />

            {/* Drawer */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="fixed top-0 right-0 bottom-0 w-80 max-w-[85vw] glass border-l border-[rgba(255,255,255,0.1)] z-50 overflow-y-auto"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-[rgba(255,255,255,0.1)]">
                <div>
                  <h2 className="text-xl font-bold text-white">ListDashes</h2>
                  <p className="text-sm text-[#A0AEC0]">Man & Machine</p>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 hover:bg-[rgba(255,255,255,0.1)] rounded-lg transition-colors"
                >
                  <X size={24} className="text-white" />
                </button>
              </div>

              {/* Navigation */}
              <div className="p-4">
                {navSections.map((section, index) => (
                  <div key={index} className="mb-2">
                    {section.single ? (
                      <Link
                        to={section.path}
                        onClick={() => setIsOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-[rgba(255,255,255,0.1)] transition-colors"
                      >
                        <section.icon size={20} className="text-[#6366F1]" />
                        <span className="text-white font-medium">{section.title}</span>
                      </Link>
                    ) : (
                      <div>
                        <button
                          onClick={() => toggleSection(section.title)}
                          className="w-full flex items-center justify-between px-4 py-3 rounded-xl hover:bg-[rgba(255,255,255,0.1)] transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <section.icon size={20} className="text-[#6366F1]" />
                            <span className="text-white font-medium">{section.title}</span>
                          </div>
                          <CaretRight
                            size={16}
                            className={`text-[#A0AEC0] transition-transform ${
                              expandedSections[section.title] ? 'rotate-90' : ''
                            }`}
                          />
                        </button>

                        <AnimatePresence>
                          {expandedSections[section.title] && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="ml-6 mt-1 space-y-1 overflow-hidden"
                            >
                              {section.items.map((item, idx) => 
                                item.action === 'open_command' ? (
                                  <div
                                    key={idx}
                                    onClick={() => { openCommandGlobal(); setIsOpen(false); }}
                                    className="flex items-center gap-3 px-4 py-2 rounded-lg transition-colors hover:bg-[rgba(255,255,255,0.05)] text-[#A0AEC0] hover:text-white cursor-pointer"
                                  >
                                    {item.icon && <item.icon size={16} />}
                                    <span className="text-sm">{item.name}</span>
                                  </div>
                                ) : (
                                <Link
                                  key={idx}
                                  to={item.path}
                                  onClick={() => !item.disabled && setIsOpen(false)}
                                  className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                                    item.disabled
                                      ? 'opacity-40 cursor-not-allowed'
                                      : 'hover:bg-[rgba(255,255,255,0.05)] text-[#A0AEC0] hover:text-white'
                                  }`}
                                >
                                  {item.icon && <item.icon size={16} />}
                                  <span className="text-sm">{item.name}</span>
                                </Link>
                                )
                              )}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    )}
                  </div>
                ))}

                {/* SignOut */}
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-3 mt-4 rounded-xl hover:bg-[rgba(255,255,255,0.1)] transition-colors border-t border-[rgba(255,255,255,0.1)] pt-4"
                >
                  <SignOut size={20} className="text-[#EF4444]" />
                  <span className="text-white font-medium">Sign Out</span>
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default MobileNav;
